// wasm-bindgen's "web" target: plain sync function exports plus a default
// `init()` that instantiates the wasm binary. `ready` is that
// initialization, started immediately below — every entry point (see the
// various main.js files, scripts/generate_default_setup.mjs) awaits this
// once before doing anything else, so no call site past that point needs to
// know the numeric core is backed by wasm at all. Deliberately not a
// top-level `await` here: Parcel's optimizer (@parcel/optimizer-swc)
// rejects top-level await outright, so the wait has to be an explicit
// promise callers opt into instead.
import init, * as wasm from "../rust/calc-core/pkg/calc_core.js";

export const ready = (async () => {
  // init()'s own default (fetching `new URL('calc_core_bg.wasm',
  // import.meta.url)`) only works in a browser — Node's fetch() doesn't
  // support file: URLs, which is exactly how this module resolves when
  // running under `node --test` (this repo's test suite) or
  // scripts/generate_default_setup.mjs. Feed it raw bytes read via node:fs
  // instead when running there; Parcel never sees this branch taken (still
  // resolves+bundles the import so the browser build stays
  // static-analyzable) and init() accepts bytes directly without touching
  // fetch either way.
  if (typeof process !== "undefined" && process.versions?.node) {
    const { readFile } = await import("node:fs/promises");
    const wasmPath = new URL("../rust/calc-core/pkg/calc_core_bg.wasm", import.meta.url);
    await init({ module_or_path: await readFile(wasmPath) });
  } else {
    await init();
  }
})();

export let radian = Math.PI / 180;
export let feet_to_m = 0.3048;
export let yard_to_m = 0.9144;
export let meter = 1000;

// Bumped whenever this file's own math changes in a way that could change a
// previously-computed fit's result — a fit computed by an older client
// (whether from this browser's own past, or pulled from another device via
// sync) must not be trusted just because it's present. See
// Calculated.isFresh() below, the one place this actually gets checked.
export const CALCULATOR_VERSION = 2;

export function toScale(unit) {
  if (unit == "m") {
    return 1;
  } else if (unit == "yd") {
    return yard_to_m;
  } else if (unit == "ft") {
    return feet_to_m;
  } else {
    throw new Error(`unknown unit ${unit}`);
  }
}

export class SightSettings {
  eye_to_sight = 1020;
  arrow_to_eye = 110;
  vertical_tpi = 24;
  horizontal_tpi = 32;
  sight_scale = 10;
  clicks_per_turn = 20;
  sight_bar_spacing = 12.7;
  sight_bar_position = 1;
  // Epoch ms of the last edit, or null if these settings have never been
  // touched (a fresh setup's defaults) — fromJson's Object.assign leaves
  // this at its default for older saved data that predates the field.
  updated_at = null;

  toString() {
    return JSON.stringify(this);
  }

  static fromJson(json) {
    const m = new SightSettings();
    return Object.assign(m, json);
  }
};

function isNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

export class Mark {
  distance = 30;
  unit = 'm';
  mark = 2.0;
  sight_bar_position = 1;
  // Epoch ms of the last edit to this row, or null if it's never been
  // touched since creation (also null for older saved data that predates
  // this field — nothing to show in that case, not "just now").
  updated_at = null;

  constructor(d, u, m, b, updated_at = null) {
    this.distance = d;
    this.unit = u;
    this.mark = m;
    this.sight_bar_position = b;
    this.updated_at = updated_at;

    if (!isNumber(d)) {
      throw new Error(`Invalid mark distance ${d}`);
    }

    if (!isNumber(toScale(u))) {
      throw new Error(`Invalid mark unit ${u}`);
    }

    if (!isNumber(m)) {
      throw new Error(`Invalid mark mark ${m}`);
    }

    if (!isNumber(b)) {
      throw new Error(`Invalid mark bar position ${b}`);
    }
  }

  inM() {
    return this.distance * toScale(this.unit);
  }

  toString() {
    return JSON.stringify(this);
  }

  static fromJson(json) {
    return new Mark(json.distance, json.unit, json.mark, json.sight_bar_position, json.updated_at ?? null);
  }
};

export class Marks {
  marks = [];

  constructor(marks) {
    this.marks = marks;
  }

  static fromJson(json) {
    return new Marks(json.map(item => Mark.fromJson(item)));
  }
}

// A setup's fitted physics model: the velocity/drag findVelocityAndDrag
// converged on, the offset calcOffset fit against the calibration marks,
// and fit itself (calcOffset's score — the mean absolute residual, lower is
// better) as a record of how good that fit actually was. Threaded through
// calculateMark/findCutDistance instead of v/drag/offset as three
// separate parameters wherever a function consumes all three together;
// functions that only need v/drag (the raw trajectory physics — calc(),
// findAngle(), maxHeight(), ...) keep taking those as plain numbers rather
// than an offset/fit they'd never use.
export class Calculated {
  v = 0;
  drag = 0;
  offset = 0;
  fit = 1;
  calculator_version = CALCULATOR_VERSION;

  constructor(v = 0, drag = 0, offset = 0, fit = 1, calculator_version = CALCULATOR_VERSION) {
    this.v = v;
    this.drag = drag;
    this.offset = offset;
    this.fit = fit;
    this.calculator_version = calculator_version;
  }

  // See CALCULATOR_VERSION's own comment — an exact match is the only thing
  // that means this file's math hasn't changed since this was computed.
  isFresh() {
    return this.calculator_version === CALCULATOR_VERSION;
  }

  toString() {
    return JSON.stringify(this);
  }

  // Persisted (localStorage) and synced (server) under the field names this
  // class predates — calc_v/calc_c/calc_offset/calc_fit — so existing saved
  // setups keep working with no migration. Only the in-memory shape uses
  // the shorter names.
  toJSON() {
    return {
      calc_v: this.v,
      calc_c: this.drag,
      calc_offset: this.offset,
      calc_fit: this.fit,
      calculator_version: this.calculator_version,
    };
  }

  static fromJson(json) {
    // calculator_version passed explicitly (not left to the constructor's
    // own default) even when absent — older saved data predating this
    // field must read as version 0 (definitely stale, triggering a
    // recompute), not silently inherit the current version by omission.
    return new Calculated(json.calc_v, json.calc_c, json.calc_offset, json.calc_fit, json.calculator_version ?? 0);
  }
}

// From here down, the numeric core (trajectory integration, angle/velocity/
// drag solvers) is a Rust/wasm module (rust/calc-core) — these are thin
// wrappers around it. SightSettings/Mark/Marks/Calculated above stay JS
// since they're JSON (de)serialization tied to localStorage/sync, not
// physics. See rust/calc-core/src/physics.rs for the actual math and its
// tests.

export function cutDists(d, cut_angle) {
  return wasm.cutDists(d, cut_angle);
}

export function angleToMark(sight_settings, sight_bar_position, a, d, cut_angle) {
  return wasm.angleToMark(sight_settings, sight_bar_position, a, d, cut_angle);
}

export function markToAngle(sight_settings, sight_bar_position, mark, d, cut_angle, tolerance = 1e-9, maxIter = 100) {
  return wasm.markToAngle(sight_settings, sight_bar_position, mark, d, cut_angle, tolerance, maxIter);
}

export function markAtDistance(d, m, sight_settings, sight_bar_position) {
  return wasm.markAtDistance(d, m, sight_settings, sight_bar_position);
}

export function roundToClick(sight_settings, num) {
  return wasm.roundToClick(sight_settings, num);
}

// calcPath returns the whole trajectory as an array of [x, y, vx, vy, t]
// points in one call, rather than invoking a JS callback per RK4 step (which
// would mean crossing the wasm boundary once per step) — this shim replays
// those points through the original per-point-callback shape so callers
// (trajectory.vue's canvas drawing) don't need to change.
export function calc(v, c, a, d, f) {
  const points = wasm.calcPath(v, c, a, d);
  for (const p of points) {
    if (!f(p)) break;
  }
}

export function estimateT(v, a, x) {
  return wasm.estimateT(v, a, x);
}

export function estimateImpact(v, c, a, d) {
  return wasm.estimateImpact(v, c, a, d);
}

export function estimateAngle(v, c, d, cut_angle) {
  return wasm.estimateAngle(v, c, d, cut_angle);
}

export function findImpact(v, c, a, d) {
  return wasm.findImpact(v, c, a, d);
}

// a_guess defaults to a fresh estimateAngle rather than requiring callers
// to compute and thread one through — see the Rust findAngle for why this
// stays cheap enough to not bother caching.
export function findAngle(v, c, d, cut_angle, a_guess = wasm.estimateAngle(v, c, d, cut_angle)) {
  return wasm.findAngle(v, c, d, cut_angle, a_guess);
}

export function calcOffset(sight_settings, data, v, c) {
  return wasm.calcOffset(sight_settings, data, v, c);
}

export function findVelocity(sight_settings, data, c, v_min, v_max) {
  const { v, drag, offset, fit } = wasm.findVelocity(sight_settings, data, c, v_min, v_max);
  return new Calculated(v, drag, offset, fit);
}

export function findVelocityAndDrag(sight_settings, data) {
  const { v, drag, offset, fit } = wasm.findVelocityAndDrag(sight_settings, data);
  return new Calculated(v, drag, offset, fit);
}

export function maxHeight(v, c, d, cut_angle) {
  return wasm.maxHeight(v, c, d, cut_angle);
}

export function calculateMark(sight_settings, sight_bar_position, calculated, cut_angle, d, a_guess = wasm.estimateAngle(calculated.v, calculated.drag, d, cut_angle)) {
  return wasm.calculateMark(sight_settings, sight_bar_position, calculated, cut_angle, d, a_guess);
}

export function marksToM(marks) {
  var data = [];
  for (let i = 0; i < marks.length; i++) {
    data.push({ d: marks[i].inM(), m: marks[i].mark, s: marks[i].sight_bar_position });
  }

  return data;
}

// The shared level-ground (cut_angle 0) mark-vs-distance table — one sweep,
// cached in wasm memory (and, via state.js's getLevelMarksTable, in
// localStorage too) rather than every consumer (Graph, Full Marks, Cuts
// Table, mark.vue's "Effective Distance") each rebuilding their own. Every
// row, for consumers that need to actually display each one.
export function levelMarksTable(sight_settings, sight_bar_position, calculated) {
  return wasm.levelMarksTable(sight_settings, sight_bar_position, calculated);
}

// Shooting at d with a nonzero cut_angle calls for the same mark as some
// level-ground shot — the "effective" or "cut" distance archers actually
// use to pick a sight mark for an uphill/downhill shot. Reuses (or builds)
// the same cached table levelMarksTable does — see
// rust/calc-core/src/physics/level_table.rs for how the search itself
// works (mark(distance) at cut_angle 0 isn't monotonic everywhere, so it's
// not a plain bisection). Returns null, not a throw, when the target mark
// is outside what that table can reach.
export function findCutDistance(sight_settings, sight_bar_position, calculated, cut_angle, d) {
  return wasm.findCutDistance(sight_settings, sight_bar_position, calculated, cut_angle, d);
}

// The whole Cuts Table grid (every distance x angle cell, downhill and
// uphill) in one call — computed entirely in Rust reusing the same cached
// table, so neither it nor the per-cell search ever cross the wasm
// boundary; only this small grid of results (meters, or null where
// unreachable) does. Returns { downhill, uphill }, each
// distances.length x angles.length.
export function cutsGrid(sight_settings, sight_bar_position, calculated, distances, angles) {
  return wasm.cutsGrid(sight_settings, sight_bar_position, calculated, distances, angles);
}

